<script setup>
import { computed } from 'vue'
import ModalBox from './ModalBox.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import { useLegacyModalBridge } from '@/composables/useLegacyModalBridge'

const props = defineProps({
	modalId: {
		type: String,
		default: 'modal-confirm',
	},
	modalSize: {
		type: String,
		default: 'sm',
		validator(v) {
			return ['xl', 'lg', 'md', 'sm'].some((size) => size === v)
		},
	},
	modalType: {
		type: String,
		default: 'main',
	},
	title: {
		type: String,
		required: false,
	},
	message: {
		type: String,
		required: false,
	},
	body: {
		type: String,
		required: false,
	},
	streamCat: {
		type: String,
	},
	okText: {
		type: String,
		default: 'Yes',
	},
	cancelText: {
		type: String,
		default: 'No',
	},
	showConfirm: {
		type: Boolean,
		default: true,
	},
	closeoutsideClick: {
		type: Boolean,
		default: false,
	},
	dismissAfterConfirm: {
		type: Boolean,
		default: true,
	},
	zIndex: {
		type: String,
		default: 'z-50',
	},
})

const emit = defineEmits(['modal-confirm', 'modal-cancel', 'modal-dismiss', 'modal-close', 'modal-hidden'])

const confirmButtonType = computed(() => {
	const map = { primary: 'main', danger: 'red', 'outline-secondary': 'secondary', secondary: 'secondary', main: 'main' }
	return map[props.modalType] ?? 'main'
})

const { isVisible, hide } = useLegacyModalBridge(
	() => props.modalId,
	{ onHidden: () => emit('modal-hidden') },
)

const dismiss = (cancel = true) => {
	if (cancel) {
		emit('modal-cancel')
	}
	hide()
	emit('modal-dismiss')
}

const closeModal = () => {
	emit('modal-close')
	hide()
}

const onConfirm = () => {
	emit('modal-confirm')
	if (props.dismissAfterConfirm) {
		dismiss(false)
	} else {
		closeModal()
	}
}

const onClose = () => {
	dismiss(true)
}
</script>

<template>
	<ModalBox
		v-model="isVisible"
		:size="modalSize"
		:z-index="zIndex"
		hide-close
		header-class="!hidden"
		wrapper-classes="lg:h-auto lg:max-h-none"
		:close-on-backdrop="!closeoutsideClick"
		@close="onClose"
	>
		<h3 v-if="title" class="px-6 pt-6 m-0 text-lg font-medium text-surface-text-2">
			{{ title }}
		</h3>
		<div class="px-6" :class="title ? 'pt-2 text-sm text-surface-7' : 'pt-6 text-surface-text-2'">
			{{ message }}{{ streamCat }}
		</div>
		<div v-if="body" class="px-6 pt-2 text-surface-7 text-sm">
			{{ body }}
		</div>
		<div class="px-6 empty:hidden">
			<slot />
		</div>
		<template #footer>
			<div class="flex justify-end gap-2 p-6">
				<FormButton type="secondary" size="md" @click="dismiss(true)">
					{{ cancelText }}
				</FormButton>
				<FormButton v-if="showConfirm" :type="confirmButtonType" size="md" @click="onConfirm">
					{{ okText }}
				</FormButton>
			</div>
		</template>
	</ModalBox>
</template>
